import { getInstanceDefinition } from '../cards/cardLookup';
import { DARKNESS_PENALTY_PER_POINT, BOARD_SIDES } from '../constants';
import {
  isWeaponEquipped, VERIS_WEAPON_BONUS, heroForEquippedWeapon, heroMeetsWeaponWeight,
} from './attackMath';

// Total Light = sum of Light icons on all present (hand) cards, excluding an
// unequipped weapon's Light value (rulebook, "Light & Darkness"). hallRankIndex
// is optional - only combat resolution (which already knows it once a monster
// is chosen) can supply it, enabling rank-conditional Light bonuses like
// Thornwood Hunter's; callers that only need a hand's flat Light total (e.g.
// Village-side checks, tests, or a preview before a monster is picked) simply
// omit it and that bonus doesn't apply, same convention as computeBaseAttack's
// own optional hallRankIndex/totalLight params.
export function computeTotalLight(state, playerId, hallRankIndex) {
  const player = state.players.find((p) => p.id === playerId);
  if (player.turnFlags.lightForcedToZero) return 0; // e.g. Curse of Horror
  let light = player.turnFlags.bonusLight || 0;
  // Veris: "Add Light +N to each equipped weapon" (see attackMath.js's
  // VERIS_WEAPON_BONUS) - summed once across every Veris copy present, then
  // added per equipped weapon encountered below.
  const verisLightBonus = player.hand.reduce(
    (sum, id) => sum + (VERIS_WEAPON_BONUS[getInstanceDefinition(state, id).id]?.light || 0), 0,
  );
  // Into the Abyss, Thornwood Hunter (L2): "Add Light +1 against the highest
  // rank." Board side determines the hall's actual rank count (3 or 4), so
  // "highest rank" is read off the hall array's own length rather than a
  // fixed number. Originally lived in attackMath.js's contributesTraits, but
  // that function's own `light` field is never consumed by computeBaseAttack
  // - moved here, to the function Light is actually read from.
  const hasThornwoodHunter = hallRankIndex !== undefined
    && hallRankIndex === state.dungeonHall.length - 1
    && player.hand.some((id) => getInstanceDefinition(state, id).id === 'hero-thornwood-l2');
  if (hasThornwoodHunter) light += 1;
  for (const instanceId of player.hand) {
    const def = getInstanceDefinition(state, instanceId);
    const isWeapon = def.traits.includes('Weapon');
    // Matt reported this directly (attackMath.js's own comment has the
    // full story): a Strength-reducing Battle effect (Guardsman's -6, e.g.)
    // can drop a hero below an already-equipped weapon's Weight mid-combat
    // - the rulebook's "you lose the benefit of its traits" applies to
    // Light exactly the same way it does to Physical/Magic Attack, so this
    // mirrors attackMath.js's own live re-check rather than trusting the
    // one-time equip-time gate.
    let equipped = isWeaponEquipped(player, instanceId);
    if (equipped && isWeapon) {
      const heroInstanceId = heroForEquippedWeapon(player, instanceId);
      const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
      if (heroDef && !heroMeetsWeaponWeight(heroDef, def)) equipped = false;
    }
    // Dancing Sword: "Even if not equipped, ... still provides light" - see
    // the matching special case in attackMath.js's contributesTraits. Not
    // itself eligible for Veris's bonus below unless actually equipped -
    // Veris's text is scoped to "equipped weapon," no exception for it.
    if (isWeapon && def.id !== 'village-dancing-sword' && !equipped) continue;
    light += def.light || 0;
    if (isWeapon && equipped) light += verisLightBonus;
    // Worlds Collide, Chalice Mace: "Add Light +1 while equipped to a
    // cleric."
    if (isWeapon && equipped && def.id === 'village-chalice-mace') {
      const heroInstanceId = heroForEquippedWeapon(player, instanceId);
      const heroDef = heroInstanceId ? getInstanceDefinition(state, heroInstanceId) : null;
      if (heroDef?.traits.includes('Cleric')) light += 1;
    }
  }
  // Root of Corruption, Circle of Protection: "It remains in play" - Brian
  // reported directly: "The light on the circle of protection is not being
  // counted when in the dungeon." Its printed Light value (1) was only ever
  // summed while it sat in hand; once placed "in front of you"
  // (player.inPlayInstanceIds - see effectHelpers.js's placeInFrontOfPlayer),
  // it fell out of the loop above entirely. Excludes the familiar (no
  // printed light field anyway) and a Mind Control-assigned monster (never
  // has one either) the same way PlayerDock.vue's own viewingInPlayInstances
  // already distinguishes "real" in-play cards from those two.
  for (const instanceId of player.inPlayInstanceIds) {
    if (instanceId === player.familiarInstanceId) continue;
    const def = getInstanceDefinition(state, instanceId);
    if (def.type === 'monster') continue;
    light += def.light || 0;
  }
  return light;
}

// Base Darkness = the fought monster's hall rank number (rank 1 = 1, etc.).
// Residual Darkness after Light offset floors at 0; the per-point penalty is a
// single flat reduction applied to the *combined* Physical+Magic total, not per
// sub-type (rulebook, "Combat resolution order").
//
// Matt's house rule: "I always play on the wilderness board but with
// dungeon board darkness levels so there are four ranks but the darkness
// penalties are still -2 per rank." hardDarkness only changes which
// per-point penalty applies - rank count/hall size/board art are entirely
// unaffected (still driven by boardSide alone, via HALL_RANK_COUNT).
export function computeDarknessPenalty({
  boardSide, hallRankIndex, totalLight, ignoreDarkness = false, hardDarkness = false,
}) {
  if (ignoreDarkness) return 0;
  const baseDarkness = hallRankIndex + 1;
  const residualDarkness = Math.max(0, baseDarkness - totalLight);
  const penaltyPerPoint = hardDarkness ? DARKNESS_PENALTY_PER_POINT[BOARD_SIDES.DUNGEON] : DARKNESS_PENALTY_PER_POINT[boardSide];
  return residualDarkness * penaltyPerPoint;
}

// Rulebook glossary: "'If there is Darkness' refers to a situation where
// there may or may not be Light, but it is not enough to completely cancel
// the Darkness" - i.e. residual Darkness > 0, independent of boardSide (both
// DARKNESS_PENALTY_PER_POINT entries are nonzero). Note this is stricter than
// "no Light present at all" - a hero with some Light can still face Darkness
// at a high enough rank, which is why this takes the actual rank rather than
// reusing a "any Light in hand" shortcut.
export function hasDarknessAtRank(state, playerId, hallRankIndex) {
  const totalLight = computeTotalLight(state, playerId, hallRankIndex);
  return (hallRankIndex + 1) - totalLight > 0;
}
